import { createTheme, type ThemeOptions } from '@mui/material/styles';
import { palette, semantic, elevation, radii, spacing, typography } from './tokens';

/**
 * MUI theme configured with Google Cloud design tokens.
 * Central place for color, typography, shape and component-level overrides.
 */
const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      light: palette.blue[300],
      main: palette.blue[600],
      dark: palette.blue[800],
      contrastText: '#ffffff',
    },
    secondary: {
      light: palette.cyan[500],
      main: palette.cyan[700],
      dark: palette.cyan[700],
      contrastText: '#ffffff',
    },
    success: { main: semantic.success, contrastText: '#ffffff' },
    warning: { main: semantic.warning, contrastText: '#ffffff' },
    error: { main: semantic.error, contrastText: '#ffffff' },
    info: { main: semantic.info, contrastText: '#ffffff' },
    grey: palette.grey,
    background: {
      default: palette.grey[50],
      paper: '#ffffff',
    },
    text: {
      primary: palette.grey[900],
      secondary: palette.grey[700],
      disabled: palette.grey[500],
    },
    divider: palette.grey[200],
  },
  shape: {
    borderRadius: radii.sm,
  },
  spacing,
  typography: {
    fontFamily: typography.fontFamily.sans,
    h1: {
      fontFamily: typography.fontFamily.display,
      fontWeight: 500,
      fontSize: '2.5rem',
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: typography.fontFamily.display,
      fontWeight: 500,
      fontSize: '2rem',
      letterSpacing: '-0.01em',
    },
    h3: { fontFamily: typography.fontFamily.display, fontWeight: 500, fontSize: '1.5rem' },
    h4: { fontFamily: typography.fontFamily.display, fontWeight: 500, fontSize: '1.25rem' },
    h5: { fontFamily: typography.fontFamily.display, fontWeight: 500, fontSize: '1.125rem' },
    h6: { fontFamily: typography.fontFamily.display, fontWeight: 500, fontSize: '1rem' },
    subtitle1: { fontWeight: 500 },
    button: { textTransform: 'none', fontWeight: 500, letterSpacing: '0.01em' },
    body1: { fontSize: '0.875rem', lineHeight: 1.5 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5, color: palette.grey[700] },
    caption: { fontSize: '0.75rem', color: palette.grey[700] },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        'html, body, #root': {
          height: '100%',
        },
        body: {
          backgroundColor: palette.grey[50],
          color: palette.grey[900],
          WebkitFontSmoothing: 'antialiased',
        },
        a: { color: palette.blue[700] },
        '*:focus-visible': {
          outline: `2px solid ${palette.blue[600]}`,
          outlineOffset: 2,
          borderRadius: 2,
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: radii.pill,
          paddingInline: 16,
          height: 36,
          // Height is fixed, so a wrapped label would overflow the pill.
          whiteSpace: 'nowrap',
        },
        containedPrimary: {
          boxShadow: 'none',
          '&:hover': { boxShadow: elevation[1] },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none' },
        outlined: { borderColor: palette.grey[200] },
      },
    },
    MuiCard: {
      defaultProps: { variant: 'outlined' },
      styleOverrides: {
        root: { borderRadius: radii.md, borderColor: palette.grey[200] },
      },
    },
    MuiAppBar: {
      defaultProps: { color: 'default', elevation: 0 },
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          borderBottom: `1px solid ${palette.grey[200]}`,
          color: palette.grey[900],
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: { borderRight: `1px solid ${palette.grey[200]}` },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 500, borderRadius: radii.sm },
      },
    },
    MuiTextField: {
      defaultProps: { size: 'small', variant: 'outlined' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: radii.sm, backgroundColor: '#ffffff' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radii.pill,
          marginInline: 8,
          '&.Mui-selected': {
            backgroundColor: palette.blue[50],
            color: palette.blue[800],
            '& .MuiListItemIcon-root': { color: palette.blue[700] },
            '&:hover': { backgroundColor: palette.blue[100] },
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: palette.grey[900],
          fontSize: '0.75rem',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: { fontWeight: 500, color: palette.grey[800], backgroundColor: palette.grey[50] },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
export { palette, semantic, elevation, radii };
